import React, { useState, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { HStack, Heading } from '@chakra-ui/react'
import { useMutation, useQueryClient } from 'react-query'
import { useStoreActions } from 'easy-peasy'
import { DataTableList } from './components'
import { Button } from '@blueprinthq/joy'
import { Loading } from '../../components'
import { endpoints } from '../../api/index'
import { MeasureTypes } from '@constants/measureTypes'
import Checkbox from '@material-ui/core/Checkbox'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import { isMobile } from 'react-device-detect'
import { Text } from '@chakra-ui/react'
import moment from 'moment'
import classes from './patientsettings.module.css'

import { Popover, TableTemplate } from '../../components'
import { doesPassAgeRule } from '../../utilities'
import flagsmith from 'flagsmith'
import { FlagsmithFeatures } from '@constants/flagsmith'

export default function CheckinModuleSelector(props) {
  const { user_id } = props

  const queryClient = useQueryClient()
  const setSnackbarMessage = useStoreActions(
    actions => actions.snackbar.setMessage
  )

  const openAssignSymptomsModal = useStoreActions(
    actions => actions.modals.assignMeasures.openModal
  )

  const openAssignWorksheetsModal = useStoreActions(
    actions => actions.modals.assignMeasures.openModal
  )

  const [symptomTrackers, setSymptomTrackers] = useState([])
  const [worksheets, setWorksheets] = useState([])

  const {
    isLoading: areCheckinModsLoading,
    data: allCheckinMods,
    refetch
  } = useQuery([endpoints.getCheckInModulesForUser.getCacheId(), user_id], () =>
    endpoints.getCheckInModulesForUser.request({ id: user_id })
  )

  const { mutateAsync: executeAssignCheckinModule } = useMutation(
    endpoints.postClinicianUserCheckinModulesAssign.request,
    {
      onError: () => {
        setSnackbarMessage({
          variant: 'error',
          message: 'There was an error editing modules'
        })
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          endpoints.getCheckInModulesForUser.getCacheId(),
          user_id
        ])
        setSnackbarMessage({
          variant: 'success',
          message: 'Saved!'
        })
      }
    }
  )

  const { mutateAsync: assignCheckInModules } = useMutation(
    endpoints.postClientAssignCheckInModules.request,
    {
      onSuccess() {
        refetch()
      }
    }
  )

  useEffect(() => {
    allCheckinMods &&
      setSymptomTrackers(allCheckinMods.filter(mod => mod.type === 'symptom'))
    allCheckinMods &&
      setWorksheets(
        allCheckinMods.filter(mod => mod.type === 'treatment_activity')
      )
  }, [allCheckinMods])

  const postCheckInData = (module_id, isChecked) => {
    const data = {
      new_value: isChecked,
      module_id
    }

    return executeAssignCheckinModule({ data, clientId: user_id })
  }

  const capitalizeFirstLetter = string => {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const renderName = measure => {
    return measure.title
  }

  const determineAction = (event, measure) => {
    if (measure.can_toggle === false) {
      return
    } else {
      handleSettingsCheckboxChange(measure.id, event)
    }
  }

  const handleSettingsCheckboxChange = (id, e) => {
    // if you click on the take now link it won't check/uncheck that assessment
    if (
      e.target.id ||
      (e.target.attributes.role &&
        e.target.attributes.role.value === 'menuitem')
    ) {
      return
    }

    let checkbox = e.target.closest('#card').querySelector('input')
    let isChecked =
      e.target.checked === undefined ? !checkbox.checked : e.target.checked
    if (isChecked === undefined) return
    return postCheckInData(id, isChecked)
  }

  const determineLink = measure => {
    const id = `link-${measure.id}`

    return (
      <a
        className={classes.link}
        href={measure.preview_url}
        target="_blank"
        rel="noopener noreferrer"
        id={id}
        aria-label={`Preview ${measure.title}`}
      >
        Preview
      </a>
    )
  }

  //for web
  const renderMeasureList = measures => {
    const { context } = props

    // Note for future dev: It may seem obvious to just shove this code into the filter code to take a loop
    // out but as of 6-12-2020 that only works for settings and not full enroll and we dont have time to
    // investigate.
    measures.forEach((measure, index) => {
      if (measure.is_screener) {
        measures.splice(index, 1)
        measures.unshift(measure)
      }
    })

    // we could use reduce to turn the double loop of filter -> map into a single pass over the measures array
    // but thats harder to read, harder to fix, and isnt a huge performance boost. If at any point the measures
    // list takes too long to load, this might be a good idea to implement.
    return measures
      .filter(measure => {
        // skip measure if it doesnt fit the rules
        /*
        if there are more rule categories besides age in the future,
        we could change the age number prop to a rules object prop
        whose keys match the keys in the rules_for_default column. 
        That way we can easily iterate over all comparisons dynamically.
        But rn its just age so its direct
      */
        const age_rules = measure.rules_for_default
          ? measure.rules_for_default.age.comparisons
          : false
        const { age } = props
        if (age_rules && age_rules.length)
          return age_rules.every(rule => doesPassAgeRule(rule, age))
        else return true
      })
      .map(measure => {
        const isChecked = measure.isActive
        const popoverRegion = (
          <div className={classes.disabled}>
            <Checkbox
              className={classes.disabled_checkbox}
              disabled
              checked={measure.isActive}
              onChange={() => {
                return
              }}
              value={measure.id}
              color="primary"
              inputProps={{
                'aria-label': 'secondary checkbox'
              }}
            />
          </div>
        )
        return (
          <TableRow
            style={{ height: measure.delivery_time ? '85px' : 'auto' }}
            key={measure.id}
            id={`card`}
            className={
              measure.can_toggle === false ? classes.disabled_row : classes.row
            }
          >
            <TableCell padding={'none'} className={classes.main_cell}>
              <div className={classes.title_sub}>
                <p className={classes.name}>{renderName(measure)}</p>
                <p>{capitalizeFirstLetter(measure.subtitle)}</p>
                {measure.delivery_time && (
                  <p className={classes.delivery_time}>
                    We&apos;ll send{' '}
                    {context.patient_info
                      ? context.patient_info.first_name
                      : 'your client'}{' '}
                    a reminder to complete this on{' '}
                    {moment(measure.delivery_time).format('M/D')}
                  </p>
                )}
              </div>
            </TableCell>
            <TableCell padding={'none'} className={classes.cell}>
              <div className={classes.name_disorder}>
                <p className={classes.name}>
                  {`${measure.num_questions} questions`}
                </p>
              </div>
            </TableCell>
            <TableCell padding={'none'} className={classes.cell}>
              <div className={classes.prev_check}>
                {determineLink(measure)}
                {measure.can_toggle === false ? (
                  <Popover
                    region={popoverRegion}
                    popoverText={
                      'This module is required and cannot be switched off.'
                    }
                  />
                ) : (
                  <Checkbox
                    className={classes.checkBox}
                    checked={isChecked}
                    onChange={e => determineAction(e, measure)}
                    value={measure.id}
                    color="primary"
                    inputProps={{
                      'aria-label': `${
                        isChecked ? 'Unassign' : 'Assign'
                      } ${measure.name || measure.title}`
                    }}
                  />
                )}
              </div>
            </TableCell>
          </TableRow>
        )
      })
  }

  // for mobile
  const renderCards = measures => {
    //if settings is true, we are in the settings menu, otherwise we are in enrollment
    const { context } = props
    const cards = measures.map(measure => {
      const popoverRegion = (
        <div className={classes.disabled}>
          <Checkbox
            className={classes.disabled_checkbox}
            disabled
            checked={measure.isActive}
            onChange={() => {
              return
            }}
            value={measure.id}
            color="primary"
            inputProps={{
              'aria-label': 'secondary checkbox'
            }}
          />
        </div>
      )

      return (
        <div className={classes.card} key={measure.id}>
          <div id="card" className={classes.card_content}>
            <CardContent>
              <p className={isMobile ? classes.name_mobile : classes.name}>
                {renderName(measure)}
              </p>
              <p
                className={
                  isMobile ? classes.disorder_mobile : classes.disorder
                }
              >
                {capitalizeFirstLetter(measure.subtitle)} |{' '}
                {`${measure.num_questions} questions`}
              </p>
              {measure.delivery_time && (
                <p className={classes.delivery_time_mobile}>
                  We&apos;ll send{' '}
                  {context.patient_info
                    ? context.patient_info.first_name
                    : 'your client'}{' '}
                  a reminder to complete this on{' '}
                  {moment(measure.delivery_time).format('M/D')}
                </p>
              )}
            </CardContent>
            <CardActions>
              {determineLink(measure)}
              <div className={classes.actions}>
                {measure.can_toggle === false ? (
                  <Popover
                    region={popoverRegion}
                    popoverText={
                      'This module is required and cannot be switched off.'
                    }
                  />
                ) : (
                  <Checkbox
                    className={classes.checkBox}
                    checked={measure.isActive}
                    onChange={e => determineAction(e, measure)}
                    value={measure.id}
                    color="primary"
                    inputProps={{
                      'aria-label': 'secondary checkbox'
                    }}
                  />
                )}
              </div>
            </CardActions>
          </div>
        </div>
      )
    })
    return <div>{cards}</div>
  }

  const isClientTabsUpdateEnabled = flagsmith.hasFeature(
    FlagsmithFeatures.CLIENT_TABS_CLEANUP
  )

  const renderMeasures = () => {
    const { type } = props
    if (type === 'symptomTrackers') {
      const activeSymptomTrackers = symptomTrackers.filter(
        tracker => tracker.isActive
      )
      const allSymptomTrackers = symptomTrackers.map(m => ({
        measureId: m.id,
        title: m.title,
        subtitle: m.subtitle,
        numQuestions: m.num_questions,
        previewUrl: m.preview_url,
        isAssigned:
          m.isActive || activeSymptomTrackers.map(sst => sst.id).includes(m.id)
      }))

      const handleAssignSymptoms = async data => {
        await assignCheckInModules({
          clientId: user_id,
          checkInModuleIds: data
        })
      }

      return (
        <>
          {!isClientTabsUpdateEnabled && (
            <>
              <Heading mb="xxsmall">Symptom Trackers</Heading>
              <Text mb="medium">
                Select symptom trackers to measure changes in mental health
                (optional).
              </Text>
              {/* <AssignSymptomsModal clientId={user_id} symptomTrackers={symptomTrackers} /> */}
              <TableTemplate
                col1_title={'SYMPTOM'}
                col2_title={'QUESTIONS'}
                content={
                  isMobile
                    ? renderCards(symptomTrackers)
                    : renderMeasureList(symptomTrackers)
                }
                isMobile={isMobile}
                settings={true}
              />
            </>
          )}
          {isClientTabsUpdateEnabled && (
            <>
              <HStack justify={'space-between'}>
                <Heading>Symptom Trackers</Heading>
                <Button
                  onClick={() => {
                    openAssignSymptomsModal({
                      clientId: user_id,
                      measureType: MeasureTypes.SYMPTOM,
                      allMeasures: allSymptomTrackers,
                      onAssign: data => {
                        const worksheetModuleIds = data.map(w => w.id)
                        handleAssignSymptoms(worksheetModuleIds)
                      }
                    })
                  }}
                >
                  Assign Symptom Trackers
                </Button>
              </HStack>
              <DataTableList
                data={activeSymptomTrackers}
                label="Assigned"
                type="Symptom Trackers"
                column="Symptom"
                onChange={(id, val) => postCheckInData(id, val)}
              />
            </>
          )}
        </>
      )
    }
    if (type === 'worksheets') {
      const activeWorkSheets = worksheets.filter(tracker => tracker.isActive)

      const allWorksheets = worksheets.map(m => ({
        measureId: m.id,
        title: m.title,
        subtitle: m.subtitle,
        numQuestions: m.num_questions,
        previewUrl: m.preview_url,
        isAssigned:
          m.isActive || activeWorkSheets.map(sw => sw.id).includes(m.id)
      }))

      const handleAssignWorksheets = async data => {
        await assignCheckInModules({
          clientId: user_id,
          checkInModuleIds: data
        })
      }
      return (
        <>
          {!isClientTabsUpdateEnabled && (
            <>
              <Heading mb="xxsmall">Worksheets</Heading>
              <Text mb="medium">
                Select worksheets you want your Client to complete (optional).
              </Text>
              <TableTemplate
                col1_title={'ACTIVITY'}
                col2_title={'QUESTIONS'}
                content={
                  isMobile
                    ? renderCards(worksheets)
                    : renderMeasureList(worksheets)
                }
                isMobile={isMobile}
                settings={true}
              />
            </>
          )}
          {isClientTabsUpdateEnabled && (
            <>
              <HStack justify={'space-between'}>
                <Heading>Worksheets</Heading>
                <Button
                  onClick={() => {
                    openAssignWorksheetsModal({
                      clientId: user_id,
                      measureType: MeasureTypes.WORKSHEET,
                      allMeasures: allWorksheets,
                      onAssign: data => {
                        const worksheetModuleIds = data.map(w => w.id)
                        handleAssignWorksheets(worksheetModuleIds)
                      }
                    })
                  }}
                >
                  Assign Worksheets
                </Button>
              </HStack>
              <DataTableList
                data={activeWorkSheets}
                label="Assigned"
                type="Worksheets"
                column="Worksheet"
                onChange={(id, val) => postCheckInData(id, val)}
              />
            </>
          )}
        </>
      )
    }
  }

  if (areCheckinModsLoading) {
    return <Loading />
  }

  return <div>{renderMeasures()}</div>
}
